/* eslint-disable max-lines */
import { useAtom } from "jotai";
import type Konva from "konva";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type InteractionMode } from "@design-lab/entities/artwork";
import { gestureStateRef, isTwoFingerGestureAtom } from "../model/state";

type UseRefineGesturesProps = {
  dimensions: { width: number; height: number };
  zoomCenter?: { x: number; y: number } | undefined;
  stageRef: React.RefObject<Konva.Stage | null>;
  contentBounds?:
    | {
        x: number;
        y: number;
        width: number;
        height: number;
      }
    | undefined;
  interactionMode: InteractionMode;
  isSpaceHeld: boolean;
  minZoom?: number;
  maxZoom?: number;
  onGestureStart?: () => void;
  onGestureEnd?: (zoom: number, position: { x: number; y: number }) => void;
};

// eslint-disable-next-line max-lines-per-function
export function useRefineGestures({
  dimensions,
  zoomCenter,
  stageRef,
  contentBounds,
  interactionMode,
  isSpaceHeld,
  minZoom = 1,
  maxZoom = 10,
  onGestureStart,
  onGestureEnd
}: UseRefineGesturesProps) {
  const [cursor, setCursor] = useState<string>("default");

  const isDragging = useRef(false);
  const dragStartPosition = useRef({ x: 0, y: 0 });
  const lastPointerPosition = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  // Touch gesture state
  const [isTwoFingerGesture, setIsTwoFingerGesture] = useAtom(isTwoFingerGestureAtom);
  const lastPinchDist = useRef<number>(0);
  const lastPinchCenter = useRef<{ x: number; y: number } | null>(null);
  const justEndedTwoFingerGesture = useRef(false);
  const singleTouchStartTimeout = useRef<number | null>(null);
  const gestureEndTimeout = useRef<number | null>(null);
  const dragEndTimeout = useRef<number | null>(null);
  const waitingForSecondFinger = useRef(false);
  const pendingTouchEvent = useRef<Konva.KonvaEventObject<TouchEvent> | null>(null);

  // Helper function to check if target should prevent panning/cursor changes
  const shouldPreventPanning = useCallback((target: Konva.Node) => {
    const targetClassName = target.className;
    const targetName = target.name();

    const isTransformerHandle =
      targetClassName === "Rect" && target.parent?.className === "Transformer";
    const isRemoveIcon = targetName.includes("remove-icon");
    const isUIControl = targetName.includes("control") || targetName.includes("button");

    return isTransformerHandle || isRemoveIcon || isUIControl;
  }, []);

  // Get cursor based on current state
  const getCursor = useCallback(() => {
    if (isDragging.current) return "grabbing";
    if (isSpaceHeld) return "grab";
    if (interactionMode === "cursor") return "default";
    if (interactionMode === "crop") return "crosshair";
    if (interactionMode === "pan") return "grab";
    return "default";
  }, [interactionMode, isSpaceHeld]);

  // Update cursor when mode or space state changes
  useEffect(() => {
    setCursor(getCursor());
  }, [getCursor]);

  // Helper function to constrain pan within content bounds
  const getConstrainedPosition = useMemo(
    () => (newX: number, newY: number, scale: number) => {
      if (!contentBounds) return { x: newX, y: newY };

      const {
        x: contentX,
        y: contentY,
        width: contentWidth,
        height: contentHeight
      } = contentBounds;
      const { width: viewportWidth, height: viewportHeight } = dimensions;

      // Calculate content bounds at current zoom level
      const scaledContentWidth = contentWidth * scale;
      const scaledContentHeight = contentHeight * scale;
      const scaledContentX = contentX * scale;
      const scaledContentY = contentY * scale;

      // Calculate viewport center
      const viewportCenterX = viewportWidth / 2;
      const viewportCenterY = viewportHeight / 2;

      // Calculate content center
      const baseCenterX = zoomCenter?.x ?? viewportWidth / 2;
      const baseCenterY = zoomCenter?.y ?? viewportHeight / 2;

      // Calculate the base position (without pan)
      const baseX = viewportCenterX - baseCenterX * scale;
      const baseY = viewportCenterY - baseCenterY * scale;

      // Extract pan from current position
      const panX = newX - baseX;
      const panY = newY - baseY;

      // Calculate content edges with current pan
      const contentLeft = baseX + scaledContentX + panX;
      const contentTop = baseY + scaledContentY + panY;
      const contentRight = contentLeft + scaledContentWidth;
      const contentBottom = contentTop + scaledContentHeight;

      // Constrain position
      let constrainedPanX = panX;
      let constrainedPanY = panY;

      // If content is smaller than viewport, keep it centered
      if (scaledContentWidth <= viewportWidth) {
        const desiredLeft = (viewportWidth - scaledContentWidth) / 2;
        constrainedPanX = desiredLeft - (baseX + scaledContentX);
      } else if (contentLeft > 0) {
        constrainedPanX = panX - contentLeft;
      } else if (contentRight < viewportWidth) {
        constrainedPanX = panX + (viewportWidth - contentRight);
      }

      if (scaledContentHeight <= viewportHeight) {
        const desiredTop = (viewportHeight - scaledContentHeight) / 2;
        constrainedPanY = desiredTop - (baseY + scaledContentY);
      } else if (contentTop > 0) {
        constrainedPanY = panY - contentTop;
      } else if (contentBottom < viewportHeight) {
        constrainedPanY = panY + (viewportHeight - contentBottom);
      }

      return {
        x: baseX + constrainedPanX,
        y: baseY + constrainedPanY
      };
    },
    [contentBounds, dimensions, zoomCenter]
  );

  // Handle mouse move for cursor changes
  const handleMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (isDragging.current) return;

      const { target } = e;

      if (shouldPreventPanning(target)) {
        setCursor("default");
      } else {
        setCursor(getCursor());
      }
    },
    [shouldPreventPanning, getCursor]
  );

  // Handle pan drag start
  const handleDragStart = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      // Prevent starting a drag if we just ended a two-finger gesture
      if (justEndedTwoFingerGesture.current) {
        return;
      }

      const { target } = e;
      const stage = target.getStage();
      if (!stage) return;

      // Allow panning if:
      // 1. Mode is 'pan'
      // 2. Space key is held
      // 3. Mode is 'cursor' AND we are clicking on empty space (the stage)
      const isStageTarget = target === stage;
      const canPan =
        interactionMode === "pan" || isSpaceHeld || (interactionMode === "cursor" && isStageTarget);

      if (!canPan) return;

      if (shouldPreventPanning(target)) return;

      const pointer = stage.getPointerPosition();
      if (pointer) {
        onGestureStart?.();
        isDragging.current = true;
        hasMoved.current = false;
        gestureStateRef.isGestureActive = true;
        gestureStateRef.hasMoved = false;
        dragStartPosition.current = { x: pointer.x, y: pointer.y };
        lastPointerPosition.current = { x: pointer.x, y: pointer.y };
        setCursor("grabbing");
      }
    },
    [interactionMode, isSpaceHeld, shouldPreventPanning, onGestureStart]
  );

  // Handle pan drag move
  const handleDragMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      // Prevent movement if we just ended a two-finger gesture or waiting for second finger
      if (
        !isDragging.current ||
        justEndedTwoFingerGesture.current ||
        waitingForSecondFinger.current
      )
        return;

      const stage = e.target.getStage();
      if (!stage) return;

      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      // Calculate distance from start position
      const distanceFromStart = Math.sqrt(
        (pointer.x - dragStartPosition.current.x) ** 2 +
          (pointer.y - dragStartPosition.current.y) ** 2
      );

      const DRAG_THRESHOLD = 5;
      if (distanceFromStart > DRAG_THRESHOLD) {
        hasMoved.current = true;
        gestureStateRef.hasMoved = true;
        e.cancelBubble = true;

        const dx = pointer.x - lastPointerPosition.current.x;
        const dy = pointer.y - lastPointerPosition.current.y;

        const currentPos = stage.position();
        const currentScale = stage.scaleX();
        const newX = currentPos.x + dx;
        const newY = currentPos.y + dy;

        const constrained = getConstrainedPosition(newX, newY, currentScale);
        stage.position(constrained);
      }

      lastPointerPosition.current = { x: pointer.x, y: pointer.y };
    },
    [getConstrainedPosition]
  );

  // Handle pan drag end
  const handleDragEnd = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (isDragging.current) {
        if (hasMoved.current) {
          e.cancelBubble = true;
          // Set flag to prevent pin placement after drag
          gestureStateRef.gestureJustEnded = true;
          if (dragEndTimeout.current !== null) {
            clearTimeout(dragEndTimeout.current);
          }
          dragEndTimeout.current = window.setTimeout(() => {
            gestureStateRef.gestureJustEnded = false;
            dragEndTimeout.current = null;
          }, 150);
        }

        isDragging.current = false;
        hasMoved.current = false;
        gestureStateRef.isGestureActive = false;
        gestureStateRef.hasMoved = false;
        setCursor(getCursor());

        const stage = stageRef.current;
        if (stage) {
          onGestureEnd?.(stage.scaleX(), stage.position());
        }
      }
    },
    [getCursor, stageRef, onGestureEnd]
  );

  // Handle mouse leave to reset cursor
  const handleMouseLeave = useCallback(() => {
    isDragging.current = false;
    setCursor("default");
  }, []);

  // Handle mouse wheel zoom
  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();

      const stage = stageRef.current;
      if (!stage) return;

      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const oldScale = stage.scaleX();
      const scaleBy = 1.1;
      const direction = e.evt.deltaY > 0 ? -1 : 1;
      const newZoom = Math.max(minZoom, Math.min(maxZoom, oldScale * scaleBy ** direction));

      if (Math.abs(newZoom - oldScale) < 0.001) return;

      onGestureStart?.();

      const stagePos = stage.position();

      // Convert pointer to content coordinates
      const mousePointTo = {
        x: (pointer.x - stagePos.x) / oldScale,
        y: (pointer.y - stagePos.y) / oldScale
      };

      // Calculate new position to keep point under cursor
      const newStageX = pointer.x - mousePointTo.x * newZoom;
      const newStageY = pointer.y - mousePointTo.y * newZoom;

      const constrained = getConstrainedPosition(newStageX, newStageY, newZoom);

      stage.scale({ x: newZoom, y: newZoom });
      stage.position(constrained);

      onGestureEnd?.(newZoom, constrained);
    },
    [minZoom, maxZoom, stageRef, getConstrainedPosition, onGestureStart, onGestureEnd]
  );

  // Handle touch start for multi-touch gestures
  const handleTouchStart = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      const { touches } = e.evt;

      if (touches.length >= 2) {
        e.evt.preventDefault();

        // Cancel any pending single-touch start
        if (singleTouchStartTimeout.current !== null) {
          clearTimeout(singleTouchStartTimeout.current);
          singleTouchStartTimeout.current = null;
        }
        waitingForSecondFinger.current = false;

        onGestureStart?.();
        setIsTwoFingerGesture(true);
        gestureStateRef.isGestureActive = true;
        gestureStateRef.hasMoved = true; // Pinch is always considered "moved"
        justEndedTwoFingerGesture.current = false;

        const t0 = touches.item(0);
        const t1 = touches.item(1);

        if (t0 && t1) {
          const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
          lastPinchDist.current = dist;

          lastPinchCenter.current = {
            x: (t0.clientX + t1.clientX) / 2,
            y: (t0.clientY + t1.clientY) / 2
          };
        }
      } else if (!justEndedTwoFingerGesture.current) {
        // Single finger touch - wait briefly to see if a second finger is coming

        // CRITICAL: Prevent default and stop propagation immediately to block erase
        e.evt.preventDefault();
        e.cancelBubble = true;

        waitingForSecondFinger.current = true;
        pendingTouchEvent.current = e;

        // Delay allowing single-finger interactions to give time for second finger
        singleTouchStartTimeout.current = window.setTimeout(() => {
          // If still waiting and no second finger came, allow the drag
          if (waitingForSecondFinger.current && pendingTouchEvent.current) {
            waitingForSecondFinger.current = false;
            // Start drag with the pending event
            handleDragStart(pendingTouchEvent.current);
            pendingTouchEvent.current = null;
          }
          singleTouchStartTimeout.current = null;
        }, 50); // 50ms is enough to detect second finger without feeling laggy
      }
    },
    [handleDragStart, onGestureStart]
  );

  // Handle touch move for pinch-to-zoom and two-finger pan
  const handleTouchMove = useCallback(
    (
      e: Konva.KonvaEventObject<TouchEvent>,
      containerRef: React.RefObject<HTMLDivElement | null>
    ) => {
      const { touches } = e.evt;

      // Block all touch moves while waiting for potential second finger
      if (waitingForSecondFinger.current) {
        e.evt.preventDefault();
        e.cancelBubble = true;
        return;
      }

      if (touches.length >= 2 && isTwoFingerGesture) {
        e.evt.preventDefault();

        const t0 = touches.item(0);
        const t1 = touches.item(1);

        if (t0 && t1 && stageRef.current && lastPinchDist.current > 0 && lastPinchCenter.current) {
          const stage = stageRef.current;

          const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
          const center = {
            x: (t0.clientX + t1.clientX) / 2,
            y: (t0.clientY + t1.clientY) / 2
          };

          const oldScale = stage.scaleX();

          // Calculate zoom change from distance between fingers
          const scale = dist / lastPinchDist.current;
          const newZoom = Math.max(minZoom, Math.min(maxZoom, oldScale * scale));

          // Calculate pan from center point movement
          const dx = center.x - lastPinchCenter.current.x;
          const dy = center.y - lastPinchCenter.current.y;

          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const relativeX = center.x - containerRect.left;
            const relativeY = center.y - containerRect.top;

            const stagePos = stage.position();

            // Calculate new position accounting for both zoom and pan
            const mousePointTo = {
              x: (relativeX - stagePos.x) / oldScale,
              y: (relativeY - stagePos.y) / oldScale
            };

            // Apply zoom transformation
            const newStageX = relativeX - mousePointTo.x * newZoom;
            const newStageY = relativeY - mousePointTo.y * newZoom;

            // Apply pan transformation
            const finalX = newStageX + dx;
            const finalY = newStageY + dy;

            const constrained = getConstrainedPosition(finalX, finalY, newZoom);

            stage.scale({ x: newZoom, y: newZoom });
            stage.position(constrained);
          }

          lastPinchDist.current = dist;
          lastPinchCenter.current = center;
        }
      } else if (!isTwoFingerGesture) {
        // Only handle single-finger drag if we're not in a two-finger gesture
        handleDragMove(e);
      }
    },
    [isTwoFingerGesture, handleDragMove, minZoom, maxZoom, getConstrainedPosition, stageRef]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      const { touches } = e.evt;

      // Transitioning from 2+ fingers to fewer fingers
      if (touches.length < 2 && isTwoFingerGesture) {
        const stage = stageRef.current;
        if (stage) {
          onGestureEnd?.(stage.scaleX(), stage.position());
        }

        // Mark that we just ended a two-finger gesture to prevent immediate single-touch interactions
        justEndedTwoFingerGesture.current = true;

        // Set flag to prevent pin placement after pinch
        gestureStateRef.gestureJustEnded = true;

        // Reset drag state to prevent leftover single-finger drag from triggering
        isDragging.current = false;
        hasMoved.current = false;
        gestureStateRef.isGestureActive = false;

        setIsTwoFingerGesture(false);
        lastPinchDist.current = 0;
        lastPinchCenter.current = null;

        // Clear the flags after a brief delay to allow new gestures
        if (gestureEndTimeout.current !== null) {
          clearTimeout(gestureEndTimeout.current);
        }
        gestureEndTimeout.current = window.setTimeout(() => {
          justEndedTwoFingerGesture.current = false;
          gestureStateRef.gestureJustEnded = false;
          gestureStateRef.hasMoved = false;
        }, 150);
      }

      // All fingers lifted
      if (touches.length === 0) {
        // Cancel any pending single-touch start
        if (singleTouchStartTimeout.current !== null) {
          clearTimeout(singleTouchStartTimeout.current);
          singleTouchStartTimeout.current = null;
        }
        waitingForSecondFinger.current = false;

        // Only call handleDragEnd if we were in a single-finger drag
        if (!justEndedTwoFingerGesture.current) {
          handleDragEnd(e);
        }
      }
    },
    [handleDragEnd, stageRef, onGestureEnd, isTwoFingerGesture]
  );

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      if (singleTouchStartTimeout.current !== null) {
        clearTimeout(singleTouchStartTimeout.current);
      }
      if (gestureEndTimeout.current !== null) {
        clearTimeout(gestureEndTimeout.current);
      }
      if (dragEndTimeout.current !== null) {
        clearTimeout(dragEndTimeout.current);
      }
    },
    []
  );

  return {
    cursor,
    handleMouseMove,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleMouseLeave,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
}
